import React, { useState, useEffect, useMemo } from 'react';
import { FaLink, FaSearch, FaExclamationTriangle } from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig';
import { Modal } from './common';
import { normalizarTexto } from '../utils/texto';
import { fechaLocal, formatFecha, iniciales, rutaPuesto } from './vacantesShared';
import './FormModal.css';
import './VacantesUI.css';

/**
 * Enlazar la vacante con alguien que ya ocupa el puesto: el caso de quien
 * ingresó sin que nadie cerrara la vacante. Su ficha no cambia.
 */
function AssignEmployeeToVacanteModal({ vacante, onClose, onSuccess }) {
  const [empleados, setEmpleados] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [elegido, setElegido] = useState(null);
  const [enlazando, setEnlazando] = useState(false);

  const sugeridos = useMemo(
    () => new Set((vacante.sugerencias_enlace || []).map((s) => s.id)),
    [vacante]
  );

  useEffect(() => {
    axiosInstance.get('/api/empleados')
      .then((response) => {
        // Mismo cargo y area que la vacante, y sin otra vacante enlazada
        const posibles = response.data
          .filter((emp) => emp.cargo_area_id === vacante.cargo_area_id && !emp.surte_vacante_id)
          .sort((a, b) => Number(sugeridos.has(b.id)) - Number(sugeridos.has(a.id))
            || (fechaLocal(b.fecha_ingreso) || 0) - (fechaLocal(a.fecha_ingreso) || 0));
        setEmpleados(posibles);
        if (posibles.length && sugeridos.has(posibles[0].id)) setElegido(posibles[0]);
      })
      .catch((err) => {
        console.error('Error fetching empleados:', err);
        setError('No se pudieron cargar los empleados');
      })
      .finally(() => setLoading(false));
  }, [vacante, sugeridos]);

  const filtrados = useMemo(() => {
    const q = normalizarTexto(busqueda).trim();
    if (!q) return empleados;
    return empleados.filter((emp) =>
      normalizarTexto(emp.nombre).includes(q) || String(emp.ficha || '').includes(q));
  }, [empleados, busqueda]);

  const handleEnlazar = async () => {
    if (!elegido) return;
    try {
      setEnlazando(true);
      setError(null);
      await axiosInstance.post(`/api/vacantes/${vacante.id}/cerrar`, { empleado_id: elegido.id });
      onSuccess();
    } catch (err) {
      console.error('Error al enlazar empleado:', err);
      setError(err.response?.data?.error || 'No se pudo enlazar el empleado');
      setEnlazando(false);
    }
  };

  return (
    <Modal isOpen={!!vacante} onClose={onClose} size="lg">
      <Modal.Header onClose={onClose}>
        <span className="flex items-center gap-2"><FaLink /> Enlazar a quien ya ocupa el puesto</span>
      </Modal.Header>
      <Modal.Body>
        <p className="text-sm text-gray-600 mb-1">
          <strong className="text-gray-900">{vacante.titulo}</strong> en {rutaPuesto(vacante)}
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Para quien ya está trabajando en este cargo. La vacante queda cubierta por esa persona,
          su ficha no cambia y quienes reportaban a la vacante pasan a reportarle a ella.
          Si es alguien nuevo, usa Registrar ingreso.
        </p>

        {error && (
          <div className="form-modal-error mb-4">
            <FaExclamationTriangle />
            {error}
          </div>
        )}

        {loading ? (
          <div className="vac-vacio"><p>Cargando empleados...</p></div>
        ) : empleados.length === 0 ? (
          <div className="vac-vacio">
            <p>Nadie ocupa hoy el cargo {vacante.cargo} sin vacante enlazada.</p>
            <p>Si la persona es nueva, registra su ingreso desde la vacante.</p>
          </div>
        ) : (
          <>
            {empleados.length > 6 && (
              <div className="vac-buscar">
                <FaSearch />
                <input
                  type="search"
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  placeholder="Buscar por nombre o ficha"
                  aria-label="Buscar empleado"
                />
              </div>
            )}
            <ul className="vac-lista" role="radiogroup" aria-label="Empleado">
              {filtrados.map((emp) => (
                <li key={emp.id}>
                  <label className={`vac-decision-opcion${elegido?.id === emp.id ? ' vac-decision-opcion--activa' : ''}`}>
                    <input
                      type="radio"
                      name="empleado"
                      checked={elegido?.id === emp.id}
                      onChange={() => setElegido(emp)}
                    />
                    <span className="vac-asiento vac-asiento--cubierta">{iniciales(emp.nombre)}</span>
                    <span className="flex-1 min-w-0">
                      <span className="vac-decision-titulo block">{emp.nombre}</span>
                      <span className="vac-decision-detalle block">
                        Ficha {emp.ficha || 'sin ficha'}
                        {emp.fecha_ingreso ? `, ingresó el ${formatFecha(emp.fecha_ingreso)}` : ''}
                      </span>
                    </span>
                    {sugeridos.has(emp.id) && (
                      <span className="text-xs font-medium text-emerald-700 whitespace-nowrap">
                        Ingresó con la vacante abierta
                      </span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={onClose}>
          Cancelar
        </button>
        <button
          className="btn btn-primary"
          disabled={!elegido || enlazando}
          onClick={handleEnlazar}
        >
          {enlazando ? 'Enlazando...' : elegido ? `Enlazar a ${elegido.nombre.split(' ')[0]}` : 'Enlazar'}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default AssignEmployeeToVacanteModal;
